import { TextSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Skeleton, Stack } from '@mui/material'
import { isEmpty } from 'lodash'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { BlankView, BlankViewImageSizes, BlankViewImageVariants } from 'src/Components/blank-view'
import { useInstallationPages } from 'src/Hooks'
import { useFarmContext } from 'src/Elements/Farm'
import { DeviceLinkModel, SystemlogModel } from 'src/Libs/installations/models'
import SystemProtocolAccordion from './SystemProtocolAccordion'
import { useTypedSelector } from 'src/State/RootReducer'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()(theme => ({
  timeline: {
    position: 'relative',
    paddingLeft: theme.spacing(1),
    '&::after': {
      left: 0,
      backgroundColor: theme.palette.primary.main,
      width: 3,
      top: 0,
      bottom: 0,
      content: '""',
      position: 'absolute',
    },
  },
  loadMoreButton: {
    display: 'flex',
    justifyContent: 'center',
  },
}))

interface Props {
  isLoadingSystemLogs: boolean
  systemlog: SystemlogModel[]
  deviceLinks: DeviceLinkModel[]
  onResolveSystemLog: (installationId: number, logId: number) => void
  showLoadMore?: boolean
  loadMore: () => void
}

/**
 * List of system protocol entries belonging to a storage (warnings and errors)
 */
const SystemProtocolList: React.FC<Props> = ({ isLoadingSystemLogs, systemlog, deviceLinks, onResolveSystemLog, showLoadMore = false, loadMore }) => {
  const [installationId] = useInstallationPages(1)
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const storageId = useTypedSelector(state => state.storage.activeStorage)
  const navigate = useNavigate()
  const redirectUrl = installationId && storageId ? `/dashboard/overview/${installationId}/${storageId}` : '/'

  if (isLoadingSystemLogs) return <SystemProtocolListSkeleton />

  if (isEmpty(systemlog))
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '80%' }} data-qa="invalid-page">
        <BlankView
          imageVariant={BlankViewImageVariants.Happy}
          imageSize={BlankViewImageSizes.Small}
          title="system-log.blank-view.title"
          explanation="system-log.blank-view.subtitle"
          action={{
            text: 'common.back-to-dashboard',
            onClick: () => navigate(redirectUrl),
          }}
        />
      </Box>
    )

  return (
    <Box>
      <Box className={classes.timeline}>
        {systemlog.map(log => {
          const serialNumber = isFarm ? deviceLinks.find(link => link.id === log.deviceLinkId)?.deviceId : undefined
          return (
            <SystemProtocolAccordion
              key={log.id}
              log={log}
              installationId={installationId}
              serialNumber={serialNumber ?? undefined}
              displaySerialNumber={isFarmMasterSelected}
              onResolveSystemLog={onResolveSystemLog}
            />
          )
        })}
      </Box>
      {showLoadMore && !isLoadingSystemLogs && (
        <Box className={classes.loadMoreButton}>
          <Button variant="contained" onClick={loadMore}>
            {t('systemlog.loadmore')}
          </Button>
        </Box>
      )}
    </Box>
  )
}

// #region Skeleton

const SKELETON_ROWS = ['top', 'middle', 'bottom']

const SystemProtocolListSkeleton: React.FC = () => (
  <Stack spacing={1}>
    {SKELETON_ROWS.map(row => (
      <Box key={row} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={16} height={16} />
        <TextSkeleton variant="body1" sx={{ flex: 1 }} />
      </Box>
    ))}
  </Stack>
)

export default SystemProtocolList
